/**
 * Account settings dialog. Reachable from the UserMenu in cloud mode.
 *
 * Four tabs:
 *   - Profile:        display name (editable) + email (read-only)
 *   - Password:       set / change the account password
 *   - Sessions:       list active sessions, revoke one or all others
 *   - Privacy & data: export personal data + delete account (GDPR)
 *
 * Mirrors OrgSettings's native <dialog> + tab pattern and reuses its CSS
 * (org-settings-*) for the shell, with a small AccountSettings.css for the
 * account-specific bits.
 */

import { useEffect, useRef, useState } from 'react';

import { useAuth } from '../auth/AuthContext.js';
import {
  type AccountApi,
  accountApi,
  AccountApiError,
  type SessionRow,
} from '../data/accountApi.js';

import './OrgSettings.css';
import './AccountSettings.css';

interface Props {
  open: boolean;
  onClose: () => void;
}

type Tab = 'profile' | 'password' | 'sessions' | 'privacy';

export function AccountSettings({ open, onClose }: Props): JSX.Element {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [tab, setTab] = useState<Tab>('profile');

  useEffect(() => {
    const d = dialogRef.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    else if (!open && d.open) d.close();
  }, [open]);

  useEffect(() => {
    const d = dialogRef.current;
    if (!d) return;
    const handler = (): void => onClose();
    d.addEventListener('close', handler);
    return () => d.removeEventListener('close', handler);
  }, [onClose]);

  const api = accountApi();

  return (
    <dialog ref={dialogRef} className="org-settings-dialog" aria-labelledby="as-title">
      <header className="org-settings-header">
        <h2 id="as-title" className="org-settings-title">
          Account settings
        </h2>
        <button type="button" className="org-settings-close" onClick={onClose} aria-label="Close">
          ×
        </button>
      </header>

      <div className="org-settings-tabs" role="tablist">
        <TabButton current={tab} value="profile" onSelect={setTab}>
          Profile
        </TabButton>
        <TabButton current={tab} value="password" onSelect={setTab}>
          Password
        </TabButton>
        <TabButton current={tab} value="sessions" onSelect={setTab}>
          Sessions
        </TabButton>
        <TabButton current={tab} value="privacy" onSelect={setTab}>
          Privacy &amp; data
        </TabButton>
      </div>

      <div className="org-settings-body">
        {tab === 'profile' && <ProfilePanel api={api} />}
        {tab === 'password' && <PasswordPanel api={api} />}
        {tab === 'sessions' && <SessionsPanel api={api} />}
        {tab === 'privacy' && <PrivacyPanel api={api} onDeleted={onClose} />}
      </div>
    </dialog>
  );
}

function TabButton({
  current,
  value,
  onSelect,
  children,
}: {
  current: Tab;
  value: Tab;
  onSelect: (t: Tab) => void;
  children: React.ReactNode;
}): JSX.Element {
  const active = current === value;
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      className={'org-settings-tab' + (active ? ' is-active' : '')}
      onClick={() => onSelect(value)}
    >
      {children}
    </button>
  );
}

/* ---------------- Profile ---------------- */

function ProfilePanel({ api }: { api: AccountApi }): JSX.Element {
  const auth = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void api
      .getProfile()
      .then((p) => {
        if (cancelled) return;
        setName(p.name ?? '');
        setEmail(p.email);
        setLoaded(true);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load profile.');
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      const trimmed = name.trim();
      await api.updateProfile({ name: trimmed === '' ? null : trimmed });
      await auth.refresh();
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save.');
    } finally {
      setBusy(false);
    }
  };

  if (!loaded && !error) return <p className="org-settings-loading">Loading…</p>;

  return (
    <form className="org-settings-panel account-form" onSubmit={(e) => void handleSubmit(e)}>
      {error && (
        <p className="org-settings-error" role="alert">
          {error}
        </p>
      )}
      <label className="account-field">
        <span className="account-label">Display name</span>
        <input
          className="input"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your name"
          maxLength={200}
        />
      </label>
      <label className="account-field">
        <span className="account-label">Email</span>
        <input className="input" type="email" value={email} readOnly aria-readonly="true" />
        <span className="account-hint">Email changes aren&apos;t supported yet.</span>
      </label>
      <div className="account-actions">
        {saved && <span className="account-saved">Saved.</span>}
        <button type="submit" className="landing-button-primary" disabled={busy}>
          {busy ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </form>
  );
}

/* ---------------- Password ---------------- */

function PasswordPanel({ api }: { api: AccountApi }): JSX.Element {
  const [hasPassword, setHasPassword] = useState<boolean | null>(null);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void api
      .getProfile()
      .then((p) => {
        if (!cancelled) setHasPassword(p.hasPassword);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load.');
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setDone(false);
    if (next.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (next !== confirm) {
      setError('New passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      await api.setPassword({
        ...(hasPassword ? { currentPassword: current } : {}),
        newPassword: next,
      });
      setCurrent('');
      setNext('');
      setConfirm('');
      setHasPassword(true);
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update password.');
    } finally {
      setBusy(false);
    }
  };

  if (hasPassword === null && !error) return <p className="org-settings-loading">Loading…</p>;

  return (
    <form className="org-settings-panel account-form" onSubmit={(e) => void handleSubmit(e)}>
      {error && (
        <p className="org-settings-error" role="alert">
          {error}
        </p>
      )}
      {done && <p className="account-saved">Password updated. Other sessions were signed out.</p>}
      {!hasPassword && (
        <p className="account-hint">
          Your account has no password yet (you sign in with a magic link or OAuth). Set one to also
          sign in with a password.
        </p>
      )}
      {hasPassword && (
        <label className="account-field">
          <span className="account-label">Current password</span>
          <input
            className="input"
            type="password"
            autoComplete="current-password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            required
          />
        </label>
      )}
      <label className="account-field">
        <span className="account-label">New password</span>
        <input
          className="input"
          type="password"
          autoComplete="new-password"
          value={next}
          onChange={(e) => setNext(e.target.value)}
          minLength={8}
          required
        />
      </label>
      <label className="account-field">
        <span className="account-label">Confirm new password</span>
        <input
          className="input"
          type="password"
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          required
        />
      </label>
      <div className="account-actions">
        <button type="submit" className="landing-button-primary" disabled={busy}>
          {busy ? 'Saving…' : hasPassword ? 'Change password' : 'Set password'}
        </button>
      </div>
    </form>
  );
}

/* ---------------- Sessions ---------------- */

function SessionsPanel({ api }: { api: AccountApi }): JSX.Element {
  const [sessions, setSessions] = useState<SessionRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = async (): Promise<void> => {
    try {
      const r = await api.listSessions();
      setSessions(r.sessions);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load sessions.');
    }
  };

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRevoke = async (id: string): Promise<void> => {
    try {
      await api.revokeSession(id);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to revoke session.');
    }
  };

  const handleRevokeOthers = async (): Promise<void> => {
    if (!window.confirm('Sign out of all other sessions?')) return;
    try {
      await api.revokeOtherSessions();
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to revoke sessions.');
    }
  };

  if (sessions === null && !error) return <p className="org-settings-loading">Loading…</p>;

  const others = (sessions ?? []).filter((s) => !s.current).length;

  return (
    <div className="org-settings-panel">
      {error && (
        <p className="org-settings-error" role="alert">
          {error}
        </p>
      )}
      <div className="account-actions account-actions-start">
        <button
          type="button"
          className="org-settings-remove"
          onClick={() => void handleRevokeOthers()}
          disabled={others === 0}
        >
          Sign out all other sessions
        </button>
      </div>
      <table className="org-settings-table">
        <thead>
          <tr>
            <th>Device</th>
            <th>IP</th>
            <th>Last seen</th>
            <th aria-label="Actions" />
          </tr>
        </thead>
        <tbody>
          {(sessions ?? []).map((s) => (
            <tr key={s.id}>
              <td>
                <div className="org-settings-member-name">
                  {shortUserAgent(s.userAgent)}
                  {s.current && <span className="account-current-badge"> · This device</span>}
                </div>
              </td>
              <td>{s.ip ?? '—'}</td>
              <td>{new Date(s.lastSeenAt).toLocaleString()}</td>
              <td>
                {!s.current && (
                  <button
                    type="button"
                    className="org-settings-remove"
                    onClick={() => void handleRevoke(s.id)}
                  >
                    Revoke
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function shortUserAgent(ua: string | null): string {
  if (!ua) return 'Unknown device';
  // Cheap, readable browser/OS hints — full UA parsing isn't worth a dep.
  const browser = ua.includes('Edg/')
    ? 'Edge'
    : ua.includes('Chrome/')
      ? 'Chrome'
      : ua.includes('Firefox/')
        ? 'Firefox'
        : ua.includes('Safari/')
          ? 'Safari'
          : 'Browser';
  const os = ua.includes('Windows')
    ? 'Windows'
    : ua.includes('Mac OS X') || ua.includes('Macintosh')
      ? 'macOS'
      : ua.includes('Android')
        ? 'Android'
        : ua.includes('iPhone') || ua.includes('iPad')
          ? 'iOS'
          : ua.includes('Linux')
            ? 'Linux'
            : '';
  return os ? `${browser} on ${os}` : browser;
}

/* ---------------- Privacy & data ---------------- */

function PrivacyPanel({ api, onDeleted }: { api: AccountApi; onDeleted: () => void }): JSX.Element {
  const auth = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const handleExport = async (): Promise<void> => {
    setExporting(true);
    setError(null);
    try {
      await api.downloadExport();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export data.');
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = async (): Promise<void> => {
    setDeleting(true);
    setError(null);
    try {
      await api.deleteAccount();
      onDeleted();
      // The cookie is cleared server-side; refresh auth to drop to the
      // signed-out state.
      await auth.refresh();
    } catch (err) {
      if (err instanceof AccountApiError && err.code === 'LAST_OWNER_OF_ORG') {
        const orgs =
          (err.details as { organizations?: { name: string }[] } | undefined)?.organizations ?? [];
        const names = orgs.map((o) => o.name).join(', ');
        setError(
          `You are the only owner of: ${names}. Transfer ownership or delete those organizations first.`,
        );
      } else {
        setError(err instanceof Error ? err.message : 'Failed to delete account.');
      }
      setDeleting(false);
    }
  };

  return (
    <div className="org-settings-panel">
      {error && (
        <p className="org-settings-error" role="alert">
          {error}
        </p>
      )}

      <section className="account-section">
        <h3 className="account-section-title">Export your data</h3>
        <p className="account-hint">
          Download a JSON file with your profile, organization memberships, and your activity log.
        </p>
        <div className="account-actions account-actions-start">
          <button
            type="button"
            className="landing-button-primary"
            onClick={() => void handleExport()}
            disabled={exporting}
          >
            {exporting ? 'Preparing…' : 'Download my data'}
          </button>
        </div>
      </section>

      <section className="account-section account-danger">
        <h3 className="account-section-title">Delete account</h3>
        <p className="account-hint">
          Permanently delete your account, sessions, and organization memberships. This cannot be
          undone.
        </p>
        {!confirming ? (
          <div className="account-actions account-actions-start">
            <button
              type="button"
              className="org-settings-remove"
              onClick={() => setConfirming(true)}
            >
              Delete my account
            </button>
          </div>
        ) : (
          <div className="account-confirm">
            <label className="account-field">
              <span className="account-label">
                Type <strong>DELETE</strong> to confirm
              </span>
              <input
                className="input"
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="DELETE"
                autoComplete="off"
              />
            </label>
            <div className="account-actions account-actions-start">
              <button
                type="button"
                className="org-settings-remove"
                onClick={() => void handleDelete()}
                disabled={confirmText !== 'DELETE' || deleting}
              >
                {deleting ? 'Deleting…' : 'Permanently delete'}
              </button>
              <button
                type="button"
                className="account-cancel"
                onClick={() => {
                  setConfirming(false);
                  setConfirmText('');
                }}
                disabled={deleting}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
